import { createBrowserRouter, Outlet, redirect, ScrollRestoration } from 'react-router'

import { PlaceholderScreen, type PlaceholderProps } from '@/features/_placeholder/PlaceholderScreen'

import { AppShell } from './AppShell'
import { RequireSession } from './RequireSession'
import { RouteError } from './RouteError'

/**
 * Every route in one file.
 *
 * Kept together deliberately: a Capacitor build loads from `file://` and needs
 * `createHashRouter` instead, and that has to be a one-line change rather than
 * a hunt through the tree.
 *
 * Data mode is what buys the three things this app needs — per-route `lazy`
 * across 51 screens, `<ScrollRestoration>` (the dashboard is 2447px tall), and
 * nested `errorElement`.
 *
 * **Sheets.** Nine screens are bottom sheets over a blurred screen. They are
 * real routes, but as *nested children* of the screen they cover rather than
 * via `backgroundLocation`. In data mode the parent stays mounted and renders
 * the sheet through its own `<Outlet/>`, which gives the same back-button and
 * deep-link behaviour while keeping the background deterministic on a cold
 * deep link — `backgroundLocation` has to invent one.
 */

function placeholder(props: PlaceholderProps) {
  return { element: <PlaceholderScreen {...props} /> }
}

function Root() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  )
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      /* --- onboarding & auth — backend §3 (no bottom nav) ----------------- */
      {
        path: '/welcome',
        lazy: async () => ({
          Component: (await import('@/features/auth/screens/SplashScreen')).SplashScreen,
        }),
      },
      {
        path: '/auth/phone',
        lazy: async () => ({
          Component: (await import('@/features/auth/screens/PhoneLoginScreen')).PhoneLoginScreen,
        }),
      },
      {
        path: '/auth/otp',
        lazy: async () => ({
          Component: (await import('@/features/auth/screens/OtpScreen')).OtpScreen,
        }),
      },
      { path: '/auth/password/create', lazy: async () => ({ Component: (await import('@/features/auth/screens/CreatePasswordScreen')).CreatePasswordScreen }) },
      { path: '/auth/login', lazy: async () => ({ Component: (await import('@/features/auth/screens/LoginPasswordScreen')).LoginPasswordScreen }) },
      { path: '/auth/password/forgot', lazy: async () => ({ Component: (await import('@/features/auth/screens/ForgotPasswordScreen')).ForgotPasswordScreen }) },
      { path: '/auth/password/reset', lazy: async () => ({ Component: (await import('@/features/auth/screens/ResetPasswordScreen')).ResetPasswordScreen }) },
      // TOTP is the other second factor §3 allows. The deck draws only the SMS
      // one, so this screen composes the same panel and code boxes at 6 digits.
      { path: '/auth/totp', lazy: async () => ({ Component: (await import('@/features/auth/screens/TotpScreen')).TotpScreen }) },
      // An invite link (core PR #25): keeps the code for signup, then goes on
      // to `/welcome`, or home for a session. Public, and deliberately not
      // behind `features.referral`, so a link shared before launch still
      // credits after it.
      { path: '/r/:code', lazy: async () => ({ Component: (await import('@/features/referral/screens/ReferralLinkRoute')).ReferralLinkRoute }) },
      /* «پشتیبانی» — backend §19, the Goftino chat and the support phone.
       * Public: the login screens link here, and so do the deposit and KYC
       * screens a guest can reach. The transaction sheet's `open_ticket`
       * lands here when it cannot open the chat itself. */
      { path: '/support', lazy: async () => ({ Component: (await import('@/features/support/screens/SupportScreen')).SupportScreen }) },

      /* --- KYC — backend §5 ---------------------------------------------- */
      { path: '/kyc', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycLevelsScreen')).KycLevelsScreen }) },
      { path: '/kyc/basic', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycIdentityScreen')).KycIdentityScreen }) },
      { path: '/kyc/bank-card', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycBankCardScreen')).KycBankCardScreen }) },
      { path: '/kyc/id-photo', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycIdPhotoScreen')).KycIdPhotoScreen }) },
      { path: '/kyc/selfie', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycSelfieScreen')).KycSelfieScreen }) },
      { path: '/kyc/address', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycAddressScreen')).KycAddressScreen }) },
      { path: '/kyc/review', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycReviewScreen')).KycReviewScreen }) },
      { path: '/kyc/rejected', lazy: async () => ({ Component: (await import('@/features/kyc/screens/KycRejectedScreen')).KycRejectedScreen }) },

      /* --- the shell: 33 screens with the bottom navigation --------------- */
      {
        element: (
          <RequireSession>
            <AppShell />
          </RequireSession>
        ),
        children: [
          { path: '/', lazy: async () => ({ Component: (await import('@/features/wallet/screens/DashboardScreen')).DashboardScreen }) },
          {
            path: '/wallet',
            lazy: async () => ({ Component: (await import('@/features/wallet/screens/WalletScreen')).WalletScreen }),
            // The transfer sheet is a child, so the wallet stays mounted behind it.
            children: [
              { path: 'transfer', lazy: async () => ({ Component: (await import('@/features/wallet/screens/TransferSheet')).TransferSheet }) },
            ],
          },
          {
            path: '/wallet/bank-cards',
            lazy: async () => ({ Component: (await import('@/features/settings/screens/BankCardsScreen')).BankCardsScreen }),
            // The add sheet is a child, so the list stays mounted behind it.
            children: [
              { path: 'add', lazy: async () => ({ Component: (await import('@/features/settings/screens/AddBankCardSheet')).AddBankCardSheet }) },
            ],
          },
          /* `/market/:symbol` used to serve both "this coin's market" and "my
           * holding of it". They are different frames (37 and 36) answering
           * different questions, so the wallet's asset rows land here instead. */
          { path: '/wallet/assets/:symbol', lazy: async () => ({ Component: (await import('@/features/wallet/screens/AssetDetailScreen')).AssetDetailScreen }) },
          { path: '/portfolio', lazy: async () => ({ Component: (await import('@/features/wallet/screens/PortfolioScreen')).PortfolioScreen }) },
          {
            path: '/reports',
            lazy: async () => ({ Component: (await import('@/features/transactions/screens/ReportsScreen')).ReportsScreen }),
            // The detail sheet is a child, so the feed stays mounted behind it.
            children: [
              { path: ':id', lazy: async () => ({ Component: (await import('@/features/transactions/screens/TransactionDetailSheet')).TransactionDetailSheet }) },
            ],
          },

          /* market — backend §7, §17 */
          {
            path: '/market',
            lazy: async () => ({ Component: (await import('@/features/market/screens/MarketListScreen')).MarketListScreen }),
            // The ⋮ sheet is a child, so the list stays mounted behind it.
            children: [
              { path: 'actions/:asset', lazy: async () => ({ Component: (await import('@/features/market/screens/CoinActionsSheet')).CoinActionsSheet }) },
            ],
          },
          /* The coin page. `:symbol` is the asset. The hub that used to live
           * here (`CryptoHubScreen`) is no longer routed. */
          { path: '/market/:symbol', lazy: async () => ({ Component: (await import('@/features/market/screens/CoinPageScreen')).CoinPageScreen }) },
          { path: '/market/:symbol/buy', lazy: async () => ({ Component: (await import('@/features/market/screens/CoinBuyScreen')).CoinBuyScreen }) },
          {
            path: '/alerts/new',
            lazy: async () => ({ Component: (await import('@/features/alerts/screens/PriceAlertScreen')).PriceAlertScreen }),
            // The coin sheet is a child, so the alert form stays mounted behind it.
            children: [
              { path: 'assets', lazy: async () => ({ Component: (await import('@/features/alerts/screens/AlertAssetSheet')).AlertAssetSheet }) },
            ],
          },

          /* «اعلان‌ها» (`924:709`) — backend §18. The app bar's bell opens it. */
          { path: '/notifications', lazy: async () => ({ Component: (await import('@/features/notifications/screens/NotificationsScreen')).NotificationsScreen }) },

          /* «پروفایل من», which the app bar's menu button opens — backend §4. */
          {
            path: '/settings',
            lazy: async () => ({ Component: (await import('@/features/settings/screens/ProfileScreen')).ProfileScreen }),
            // The logout confirm is a child, so the hub stays mounted behind it.
            children: [
              { path: 'logout', lazy: async () => ({ Component: (await import('@/features/settings/screens/LogoutSheet')).LogoutSheet }) },
            ],
          },

          /* «اطلاعات شخصی» — a pushed screen, so the bottom nav stays as drawn. */
          { path: '/settings/personal', lazy: async () => ({ Component: (await import('@/features/settings/screens/PersonalInfoScreen')).PersonalInfoScreen }) },

          /* «امنیت حساب» (`924:601`) — backend §6 and §18's `/devices`, on
           * fixtures until core builds them. The hub's «امنیت حساب» row and
           * its two-step tile land here. Every sheet is a child, so the
           * screen stays mounted behind it and back closes it. */
          {
            path: '/settings/security',
            lazy: async () => ({ Component: (await import('@/features/security/screens/SecurityScreen')).SecurityScreen }),
            children: [
              { path: 'password', lazy: async () => ({ Component: (await import('@/features/security/screens/ChangePasswordSheet')).ChangePasswordSheet }) },
              { path: 'confirm/:action', lazy: async () => ({ Component: (await import('@/features/security/screens/ConfirmCodeSheet')).ConfirmCodeSheet }) },
              { path: 'totp', lazy: async () => ({ Component: (await import('@/features/security/screens/TotpSetupSheet')).TotpSetupSheet }) },
              { path: 'devices/:id/remove', lazy: async () => ({ Component: (await import('@/features/security/screens/RemoveDeviceSheet')).RemoveDeviceSheet }) },
            ],
          },

          /* «دعوت از دوستان» (`924:221`) — core PR #25's `/referral`. The hub's
           * row is gated on `features.referral`, and so is this: with the flag
           * off the URL redirects to `/`, as the gated crypto routes do. */
          {
            path: '/settings/referral',
            lazy: async () => {
              const [{ featureRoute }, { ReferralScreen }] = await Promise.all([
                import('./featureRoute'),
                import('@/features/referral/screens/ReferralScreen'),
              ])
              return featureRoute('referral', ReferralScreen)
            },
          },

          /* The portfolio's fee card links here; §20 has no frame in the deck. */
          { path: '/settings/fees', lazy: async () => ({ Component: (await import('@/features/settings/screens/FeeTiersScreen')).FeeTiersScreen }) },

          /* §21 serves guides by slug, so one route covers the trade screen's
           * "راهنمای تصویری" and every future guide. */
          { path: '/guides/:slug', lazy: async () => ({ Component: (await import('@/features/content/screens/GuideScreen')).GuideScreen }) },

          /* trade — backend §9. The sheet states (26, 31, 40, 43, 45–48, 50)
           * are children, so the trade screen stays mounted behind them. */
          {
            path: '/trade',
            lazy: async () => ({ Component: (await import('@/features/trade/screens/TradeScreen')).TradeScreen }),
            children: [
              // The confirm sheet is a child so the form stays mounted behind it.
              { path: 'confirm', lazy: async () => ({ Component: (await import('@/features/trade/screens/TradeConfirmRoute')).TradeConfirmRoute }) },
              { path: 'assets', lazy: async () => ({ Component: (await import('@/features/market/screens/AssetPickerSheet')).AssetPickerSheet }) },
              { path: 'chart', lazy: async () => ({ Component: (await import('@/features/market/screens/PriceChartSheet')).PriceChartSheet }) },
            ],
          },
          { path: '/trade/receipt/:id', lazy: async () => ({ Component: (await import('@/features/trade/screens/TradeReceiptScreen')).TradeReceiptScreen }) },
        ],
      },

      /* --- deposit & withdrawal — backend §11–§15 ------------------------- */
      { path: '/deposit', lazy: async () => ({ Component: (await import('@/features/deposit/screens/DepositHubScreen')).DepositHubScreen }) },
      // The gateway and identifier rails are gone (§11), and so are their
      // screens. Links already out there land on the hub.
      { path: '/deposit/gateway', loader: () => redirect('/deposit') },
      { path: '/deposit/identifier', loader: () => redirect('/deposit') },
      /* Gated like `/withdraw/amount`: with `features.toman_deposit` off both
       * redirect home rather than meeting the service's `404`. */
      {
        path: '/deposit/bank-transfer',
        lazy: async () => {
          const [{ featureRoute }, { BankTransferScreen }] = await Promise.all([
            import('./featureRoute'),
            import('@/features/deposit/screens/TransferDepositScreen'),
          ])
          return featureRoute('toman_deposit', BankTransferScreen)
        },
      },
      {
        path: '/deposit/card-to-card',
        lazy: async () => {
          const [{ featureRoute }, { CardToCardScreen }] = await Promise.all([
            import('./featureRoute'),
            import('@/features/deposit/screens/TransferDepositScreen'),
          ])
          return featureRoute('toman_deposit', CardToCardScreen)
        },
      },
      /* Not gated, as `/withdraw/:id` is not: a deposit already made stays
       * readable with the flag off. The static segments above rank over it. */
      { path: '/deposit/:id', lazy: async () => ({ Component: (await import('@/features/deposit/screens/DepositStatusScreen')).DepositStatusScreen }) },
      // The gateway's return trip, for links already out there.
      { path: '/deposit/receipt/:id', loader: ({ params }) => redirect(`/deposit/${params.id ?? ''}`) },
      /* Gated, as `/withdraw/crypto` is: with `features.toman_withdrawal` off
       * the form redirects home rather than meeting the service's `404`. */
      {
        path: '/withdraw/amount',
        lazy: async () => {
          const [{ featureRoute }, { WithdrawAmountScreen }] = await Promise.all([
            import('./featureRoute'),
            import('@/features/withdraw/screens/WithdrawAmountScreen'),
          ])
          return featureRoute('toman_withdrawal', WithdrawAmountScreen)
        },
      },
      /* Not gated: a withdrawal in flight stays readable and cancellable with
       * the flag off. `/withdraw/amount` and `/withdraw/crypto` are static
       * segments, so they rank above `:id`. */
      { path: '/withdraw/:id', lazy: async () => ({ Component: (await import('@/features/withdraw/screens/WithdrawStatusScreen')).WithdrawStatusScreen }) },
      // The status screen's first path, for links already out there.
      { path: '/withdraw/queued/:id', loader: ({ params }) => redirect(`/withdraw/${params.id ?? ''}`) },

      /* --- flag-gated: §22 says these cannot ship as drawn ---------------- */
      {
        path: '/deposit/crypto',
        lazy: async () => {
          const { featureRoute } = await import('./featureRoute')
          return featureRoute('crypto_deposit', {
            title: 'واریز رمز ارز',
            node: '202:1272',
            backend: '§13',
            note: 'پشت فلگ features.crypto_deposit — صرافی کیف پول ندارد (§22).',
          })
        },
      },
      {
        path: '/withdraw/crypto',
        lazy: async () => {
          const { featureRoute } = await import('./featureRoute')
          return featureRoute('crypto_withdrawal', {
            title: 'برداشت رمز ارز',
            node: '202:1173',
            backend: '§14',
            note: 'پشت فلگ features.crypto_withdrawal — صرافی کیف پول ندارد (§22).',
          })
        },
      },
      {
        path: '/orders',
        lazy: async () => {
          const { featureRoute } = await import('./featureRoute')
          return featureRoute('orderbook_trading', {
            title: 'معاملات حرفه‌ای',
            node: '202:2196',
            backend: '§10',
            note: 'پشت فلگ features.orderbook_trading — بک‌اند موتور تطبیق ندارد (§22).',
          })
        },
      },

      /* --- catch-all ------------------------------------------------------ */
      { path: '*', ...placeholder({ title: 'صفحه پیدا نشد' }) },
    ],
  },
])
